import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

export interface MarketingCampaignEmail {
  id: number;
  campaign_name: string;
  campaign_type: string;
  subject_line: string;
  html_body: string;
  text_body: string | null;
  status: 'draft' | 'scheduled' | 'sent' | 'archived';
  brevo_list_ids: number[];
  estimated_recipients: number;
  ai_context: string | null;
  metadata: { brevoCampaignId?: number; testPassedAt?: string; sendHistory?: unknown[]; totalSends?: number };
  created_by: string;
  campaign_id: string | null;
  created_at: string;
  updated_at: string;
  sent_at: string | null;
  sent_by: string | null;
}

export interface MarketingCampaignEmailDraft {
  campaign_name?: string;
  campaign_type?: string;
  subject_line?: string;
  html_body?: string;
  text_body?: string | null;
  brevo_list_ids?: number[];
  ai_context?: string | null;
  campaign_id?: string | null;
}

const BASE = '/brevo/marketing-campaigns';

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['marketing-campaigns-email'] });
}

export function useMarketingCampaignsEmail() {
  return useQuery({
    queryKey: ['marketing-campaigns-email'],
    queryFn: async () => (await apiClient<{ campaigns: MarketingCampaignEmail[] }>(BASE)).campaigns,
  });
}

export function useCreateMarketingCampaignEmail() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (draft: MarketingCampaignEmailDraft) =>
      (await apiClient<{ campaign: MarketingCampaignEmail }>(BASE, { method: 'POST', body: JSON.stringify(draft) })).campaign,
    onSuccess: invalidate,
  });
}

export function useUpdateMarketingCampaignEmail() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, ...fields }: MarketingCampaignEmailDraft & { id: number }) =>
      (await apiClient<{ campaign: MarketingCampaignEmail }>(`${BASE}/${id}`, { method: 'PUT', body: JSON.stringify(fields) })).campaign,
    onSuccess: invalidate,
  });
}

export interface GenerateDraftInput {
  context: string;
  campaign_id: string;
  campaign_name: string;
  campaign_type: string;
}

// The n8n marketing-email-generator workflow reads campaign_id / campaign_name /
// campaign_type / context off the flat webhook body (its "Date" node wraps the
// whole body as `context`, and the AI + upsert nodes read context.campaign_id
// etc.). The old admin sent all of these; the rebuild dropped everything but
// `context`, leaving those fields undefined in n8n — so send them all.
export function useGenerateMarketingEmailDraft() {
  return useMutation({
    mutationFn: (input: GenerateDraftInput) =>
      apiClient<{ subject: string; html: string; text: string }>(`${BASE}/generate-draft`, {
        method: 'POST',
        body: JSON.stringify({
          campaign_id: input.campaign_id,
          campaign_name: input.campaign_name,
          campaign_type: input.campaign_type,
          type: 'marketing_campaign',
          context: input.context,
          tone: 'professional_friendly',
          include_cta: true,
          format: 'html_and_text',
        }),
      }),
  });
}

export function useSendTestMarketingCampaignEmail() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, testEmails, listIds }: { id: number; testEmails: string[]; listIds: number[] }) =>
      apiClient<{ testPassedAt: string; campaign: MarketingCampaignEmail }>(`${BASE}/${id}/send-test`, {
        method: 'POST',
        body: JSON.stringify({ testEmails, listIds }),
      }),
    onSuccess: invalidate,
  });
}

export function useSendMarketingCampaignEmail() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, listIds, overrideSent }: { id: number; listIds: number[]; overrideSent?: boolean }) =>
      apiClient<{ campaign: MarketingCampaignEmail }>(`${BASE}/${id}/send`, {
        method: 'POST',
        body: JSON.stringify({ listIds, confirmSend: true, overrideSent }),
      }),
    onSuccess: invalidate,
  });
}

export function useMarketingCampaignEmailRecipientCount(id: number, listIds: number[]) {
  return useQuery({
    queryKey: ['marketing-campaign-email-recipient-count', id, listIds],
    queryFn: () => apiClient<{ count: number }>(`${BASE}/${id}/recipient-count?listIds=${listIds.join(',')}`),
    enabled: listIds.length > 0,
  });
}
